import UserAuditHistory from './UserAuditHistory'
import React, { useEffect, useMemo, useState } from 'react'
import { listUsersFS, upsertUserFS, adminSetTempPasswordFS } from '@/services/firestore/usersService'
import { listCommunitiesFS } from '@/services/firestore/communityService'
import AdminBar from '../../components/AdminBar.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const COMPANY_DOMAINS = ['scullycompany.com', 'portocallhotel.com']
const ALL_TITLES = [
  'Manager','Regional Manager','Assistant Director of Facilities','Director of Facilities','Head of Maintenance',
  'Assistant Manager','Service Manager','President','CTO',
  'VP / Facilities','VP / Operations','VP / Finance','CFO','Operations Admin','Accounting','Owner (3P)'
]
const ALL_ROLES = ['requester','approver','viewer']
const ADMIN_LEVELS = ['none','admin','sysconfig']

function DirectoryDetails({directory}) {
  if(!directory)return <span className="muted">Not synced</span>
  return <div className="directory-details">{[['Job title',directory.jobTitle],['Department',directory.department],['Office',directory.officeLocation]].map(([label,value])=><div key={label}><span className="directory-label">{label}</span><span>{value||'—'}</span></div>)}{directory.accountEnabled===false&&<span className="muted">Disabled in Microsoft</span>}</div>
}

function emailDomainOk(email=''){
  const d = String(email).split('@')[1] || ''
  return COMPANY_DOMAINS.includes((d || '').toLowerCase())
}
function sortByName(a,b){
  return (a.displayName||'').localeCompare(b.displayName||'')
}
function generateTempPassword(len = 12){
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$'
  const arr = new Uint32Array(len)
  crypto.getRandomValues(arr)
  let out = ''
  for (let i = 0; i < len; i++) out += alphabet[arr[i] % alphabet.length]
  return out
}

const emptyForm = {
  email: '',
  displayName: '',
  title: 'Manager',
  roles: ['requester'],
  adminLevel: 'none',
  communities: [],      // display names
  communityCodes: [],   // UPPERCASE codes used by Storage rules
  allCommunities: false,
  status: 'active',
  isActive: true,
}

export default function UsersAdminDev(){
  const [users, setUsers] = useState([])
  const [communities, setCommunities] = useState([])
  const [q, setQ] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState(null) // { index, data }
  const [error, setError] = useState('')
  const [confirmArch, setConfirmArch] = useState({ open:false, key:null })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [resetDlg, setResetDlg] = useState({
    open: false, email: '', displayName: '', temp: '', working: false, message: ''
  })

  // NEW: initial temp password for *new* users only

  const session = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('session') || '{}') || {}
    } catch {
      return {}
    }
  }, [])
  const currentUserEmail = String(session?.email || '').toLowerCase()
  const currentUserIsAdmin = ['admin','sysconfig'].includes(String(session?.adminLevel || '').toLowerCase())

  // ---------- helpers ----------
  const safeKey = (row) => (row?.id || row?.email || '').toLowerCase()
  const indexOfUser = (rowOrEmail) => {
    const key = (typeof rowOrEmail === 'string'
      ? rowOrEmail
      : (rowOrEmail?.id || rowOrEmail?.email || '')
    ).toLowerCase()
    if (!key) return -1
    return users.findIndex(u => safeKey(u) === key)
  }

  // code -> display name
  const codeToName = useMemo(() => {
    const m = new Map()
    for (const c of (communities || [])) {
      const code = (c.code || c.id || '').toString().toUpperCase()
      const name = c.communityName || c.name || code
      if (code) m.set(code, name)
    }
    return m
  }, [communities])

  // display name -> CODE (UPPERCASE)
  const nameToCode = useMemo(() => {
    const m = new Map()
    for (const c of (communities || [])) {
      const code = (c.code || c.id || '').toString().toUpperCase()
      const name = (c.communityName || c.name || code).toLowerCase()
      if (name && code) m.set(name, code)
    }
    return m
  }, [communities])

  const allNames = useMemo(() => {
    const set = new Set()
    for (const c of (communities || [])) {
      const name = c.communityName || c.name
      if (name) set.add(name)
    }
    return set
  }, [communities])

  // Normalize any mixture of codes / names to clean display names
  function normalizeCommunitiesToNames(list) {
    const src = Array.isArray(list) ? list : []
    const out = []
    for (const v of src) {
      if (!v) continue
      const s = String(v)
      if (allNames.has(s)) { out.push(s); continue }                 // already a name
      const mapped = codeToName.get(s.toUpperCase())                 // code -> name
      if (mapped) { out.push(mapped); continue }
      out.push(s)                                                    // last resort: store as-is
    }
    return Array.from(new Set(out)).sort((a,b)=>a.localeCompare(b))
  }

  // Derive codes from names (UPPERCASE)
  function namesToCodes(names){
    const uniq = new Set()
    for (const n of (names || [])) {
      const code = nameToCode.get(String(n).toLowerCase())
      if (code) uniq.add(code.toUpperCase())
    }
    return Array.from(uniq)
  }

  // ---------- load ----------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true)
        const [u, comms] = await Promise.all([ listUsersFS(), listCommunitiesFS() ])
        const commList = (Array.isArray(comms) ? comms : []).map(c => ({
          id: c.id || c.code || c.communityName || '',
          code: (c.code || '').toUpperCase(),
          communityName: c.communityName || c.name || c.code || '',
        })).filter(c => c.communityName)
        setCommunities(commList)

        const normalizedUsers = (u || []).map(row => {
          const names = normalizeCommunitiesToNames(row.communities)
          const codes = Array.isArray(row.communityCodes) && row.communityCodes.length
            ? Array.from(new Set(row.communityCodes.map(x => String(x).toUpperCase())))
            : namesToCodes(names)
          return { ...row, communities: names, communityCodes: codes, allCommunities: row.allCommunities === true }
        })
        setUsers(normalizedUsers.slice().sort(sortByName))
      } catch (e) {
        console.error('[UsersAdmin] load failed', e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // ---------- CRUD ----------
  function onNew(){
    setError('')
    setEditing({ index: -1, data: { ...emptyForm } })
  }

  function onEdit(row){
    setError('')
    const idx = indexOfUser(row)
    if (idx < 0) { setError('Could not locate user to edit.'); return }
    setEditing({ index: idx, data: JSON.parse(JSON.stringify(users[idx])) })
  }

  function onCancel(){
    setEditing(null)
    setError('')
  }

  async function onSave(){
    const u = editing.data
    if (!u.email || !u.displayName){
      setError('Email and Display Name are required.')
      return
    }
    if (!emailDomainOk(u.email)){
      setError('Only company emails are allowed (scullycompany.com or portocallhotel.com).')
      return
    }

    const editingSelf =
      currentUserEmail &&
      String(u.email || '').toLowerCase() === currentUserEmail
    if (editingSelf && currentUserIsAdmin && !['admin','sysconfig'].includes(String(u.adminLevel || '').toLowerCase())){
      setError('You cannot remove your own admin access. Ask another administrator to make that change if needed.')
      return
    }

    u.roles = Array.from(new Set(u.roles || [])).filter(r => ALL_ROLES.includes(r))
    if (u.roles.length === 0) u.roles = ['viewer']

    if (u.status === 'archived') u.isActive = false
    else if (u.status === 'inactive') u.isActive = false
    else { u.status = 'active'; u.isActive = true }

    // Normalize names and derive codes every save
    u.communities = normalizeCommunitiesToNames(u.communities)
    u.communityCodes = namesToCodes(u.communities)
    u.allCommunities = u.allCommunities === true

    if (editing.index < 0) {
      const exists = users.find(x => (x.email || '').toLowerCase() === u.email.toLowerCase())
      if (exists){ setError('A user with that email already exists.'); return }
    }

    try {
      setSaving(true)
      if(editing.index<0){u.authMethod='microsoft';u.requirePasswordChange=false}
      await upsertUserFS({ ...u, id: (u.id || u.email?.toLowerCase()) })

      const next = users.slice()
      if (editing.index >= 0){
        next[editing.index] = u
      } else {
        next.push(u)
      }
      setUsers(next.slice().sort(sortByName))
      setEditing(null)
    } catch (e) {
      console.error('[UsersAdmin] save failed', e)
      setError(e?.message || 'Failed to save user.')
    } finally {
      setSaving(false)
    }
  }

  async function setStatusByIndex(row, status){
    const idx = indexOfUser(row)
    if (idx < 0) return
    const target = users[idx]
    const updated = {
      ...target,
      status,
      isActive: status === 'active',
    }
    // keep names normalized and keep codes in sync
    updated.communities = normalizeCommunitiesToNames(updated.communities)
    updated.communityCodes = namesToCodes(updated.communities)

    try {
      setSaving(true)
      await upsertUserFS({ ...updated, id: (updated.id || updated.email?.toLowerCase()) })
      const next = users.slice()
      next[idx] = updated
      setUsers(next.slice().sort(sortByName))
    } catch (e) {
      console.error('[UsersAdmin] status update failed', e)
    } finally {
      setSaving(false)
    }
  }

  function activate(row){ return setStatusByIndex(row, 'active') }
  function deactivate(row){ return setStatusByIndex(row, 'inactive') }

  function requestArchiveByEmail(email){ setConfirmArch({ open:true, key: email }) }
  async function finalizeArchive(){
    const i = indexOfUser(confirmArch.key)
    if (i >= 0){
      await setStatusByIndex(users[i], 'archived')
    }
    setConfirmArch({ open:false, key:null })
  }

  // ---------- reset password ----------
  function openResetPassword(u){
    setResetDlg({
      open: true,
      email: u.email,
      displayName: u.displayName || '',
      temp: generateTempPassword(),
      working: false,
      message: ''
    })
  }
  function closeResetPassword(){
    setResetDlg({ open:false, email:'', displayName:'', temp:'', working:false, message:'' })
  }
  async function confirmResetPassword(){
    try {
      setResetDlg(s => ({ ...s, working: true, message: '' }))
      const temp = resetDlg.temp || generateTempPassword()
      await adminSetTempPasswordFS(resetDlg.email, temp)
      setResetDlg(s => ({ ...s, working: false, message: 'Temporary password set.' }))
      const next = await listUsersFS()
      setUsers(next.slice().sort(sortByName))
    } catch (e) {
      console.error('[UsersAdmin] reset password failed', e)
      setResetDlg(s => ({ ...s, working: false, message: e?.message || 'Failed to set temp password.' }))
    }
  }

  // ---------- filtering ----------
  const matchesQ = (u) => {
    const s = q.trim().toLowerCase()
    if (!s) return true
    return (
      (u.displayName||'').toLowerCase().includes(s) ||
      (u.email||'').toLowerCase().includes(s) ||
      (u.title||'').toLowerCase().includes(s) ||
      (u.communities||[]).join(' ').toLowerCase().includes(s)
    )
  }

  const activeRows   = useMemo(() => users.filter(u => u.status !== 'archived' && matchesQ(u)), [users, q])
  const archivedRows = useMemo(() => users.filter(u => u.status === 'archived' && matchesQ(u)), [users, q])

  const archUser = confirmArch.key ? users.find(x => x.email?.toLowerCase() === confirmArch.key?.toLowerCase()) : null

  const communityNameList = useMemo(() => {
    return Array.from(allNames.values()).sort((a,b)=>a.localeCompare(b))
  }, [allNames])

  return (
    <div>
      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>Users</span>
</h2>
      <AdminBar title="" />

      {/* Toolbar */}
      <div className="card" style={{display:'flex', gap:12, alignItems:'center', flexWrap:'wrap'}}>
        <input
          placeholder="Search name / email / title / community"
          value={q}
          onChange={e=>setQ(e.target.value)}
          style={{flex:1, minWidth:260}}
        />
        <label style={{display:'inline-flex', alignItems:'center', gap:6}}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e=>setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
        <button onClick={onNew} disabled={saving}>+ New User</button>
      </div>

      {/* Edit / Create panel */}
      {editing && (
        <div className="card" style={{marginTop:12}}>
          <h3>{editing.index >= 0 ? 'Edit User' : 'Create User'}</h3>
          {error && <div style={{color:'#b94a48', marginBottom:8}}>{error}</div>}
          {currentUserIsAdmin && String(editing.data.email || '').toLowerCase() === currentUserEmail && (
            <div className="muted" style={{marginBottom:8}}>
              Your own admin access cannot be removed from this screen.
            </div>
          )}

          <div className="grid2" style={{gap:12}}>
            <div>
              <label>Email</label>
              <input
                value={editing.data.email}
                onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, email: e.target.value.trim() } }))}
                placeholder="firstname.lastname@scullycompany.com"
              />
              <div className="muted" style={{fontSize:12}}>
                Company emails only: scullycompany.com, portocallhotel.com
              </div>
            </div>
            <div>
              <label>Display Name</label>
              <input
                value={editing.data.displayName}
                onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, displayName: e.target.value } }))}
              />
            </div>

            <div>
              <label>Title</label>
              <select
                value={editing.data.title}
                onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, title: e.target.value } }))}
              >
                {ALL_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label>Admin Level</label>
              <select
                value={editing.data.adminLevel}
                onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, adminLevel: e.target.value } }))}
              >
                {ADMIN_LEVELS.map(a => {
                  const editingSelf =
                    currentUserIsAdmin &&
                    String(editing.data.email || '').toLowerCase() === currentUserEmail
                  const disableOption = editingSelf && a === 'none'
                  return <option key={a} value={a} disabled={disableOption}>{a}</option>
                })}
              </select>
            </div>

            <div>
              <label>Roles</label>
              <div className="pillWrap">
                {ALL_ROLES.map(r => {
                  const checked = (editing.data.roles || []).includes(r)
                  return (
                    <label key={r} className="pill" style={{cursor:'pointer'}}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={()=>{
                          const cur = new Set(editing.data.roles || [])
                          cur.has(r) ? cur.delete(r) : cur.add(r)
                          setEditing(s => ({ ...s, data: { ...s.data, roles: Array.from(cur) } }))
                        }}
                        style={{marginRight:6}}
                      />
                      {r}
                    </label>
                  )
                })}
              </div>
            </div>

            <div>
              <label>Status</label>
              <select
                value={editing.data.status}
                onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, status: e.target.value } }))}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="archived">archived</option>
              </select>
            </div>

            <div className="full">
              <label style={{display:'inline-flex', alignItems:'center', gap:8, marginBottom:8}}>
                <input
                  type="checkbox"
                  checked={editing.data.allCommunities === true}
                  onChange={e=>setEditing(s => ({ ...s, data: { ...s.data, allCommunities: e.target.checked } }))}
                />
                All Communities access
              </label>
              <div className="muted" style={{fontSize:12, marginBottom:8}}>
                Grants access to current and future communities automatically.
              </div>
              <label>Communities (hold Cmd/Ctrl to multi-select)</label>
              <select
                multiple
                value={editing.data.communities || []}
                disabled={editing.data.allCommunities === true}
                onChange={e=>{
                  const vals = Array.from(e.target.selectedOptions).map(o=>o.value)
                  setEditing(s => ({ ...s, data: { ...s.data, communities: vals } }))
                }}
                style={{minHeight:140, width:'100%'}}
              >
                {communityNameList.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="muted" style={{fontSize:12, marginTop:4}}>
                {editing.data.allCommunities === true
                  ? <>This user will automatically inherit access to all current and future communities.</>
                  : <>Stores community <strong>names</strong> on the user and auto-syncs the <strong>communityCodes</strong> used by Storage rules.</>}
              </div>
            </div>

            {editing.index < 0 && <p className="muted">New users sign in with Microsoft. MaintainIQ passwords are not issued.</p>}
          </div>

          <div style={{marginTop:12, display:'flex', gap:8}}>
            <button onClick={onSave} disabled={saving}>Save</button>
            <button onClick={onCancel} className="secondary" disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="card" style={{marginTop:12}}>
          <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table users-table users-directory-table" style={{ width: '100%' }}>
            <colgroup>
              {[20,10,21,8,14,7,20].map((width,i)=><col key={i} style={{width:`${width}%`}}/>)}
            </colgroup>

            <thead>
              <tr>
                <th>User</th>
                <th>MaintainIQ title</th>
                <th>Microsoft directory</th>
                <th>Roles</th>
                <th>Communities</th>
                <th>Status</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {activeRows.map((u) => (
                <tr key={u.email || u.id}>
                  <td>
                    <div><strong>{u.displayName}</strong></div>
                    <div className="muted">{u.email}</div>
                  </td>

                  <td className="user-title">{u.title || "—"}</td>
                  <td className="user-directory"><DirectoryDetails directory={u.directory}/></td>

                  <td style={{ overflow:'visible', textOverflow:'clip', whiteSpace:'nowrap' }}>
                    <div className="pillWrap">
                      {(u.roles||[]).map(r => <span className="pill" key={r}>{r}</span>)}
                    </div>
                  </td>

                  <td style={{ overflow:'visible', textOverflow:'clip', whiteSpace:'nowrap' }}>
                    <div className="pillWrap">
                      {u.allCommunities === true
                        ? <span className="pill">All Communities</span>
                        : (u.communities||[]).map(n => <span className="pill" key={n}>{n}</span>)}
                    </div>
                  </td>

                  <td style={{ overflow:'visible', textOverflow:'clip', whiteSpace:'nowrap' }}>
                    <span className="badge" style={{ padding:'2px 10px', minWidth:64, display:'inline-block', textAlign:'center' }}>
                      {u.status}
                    </span>
                  </td>

                  <td className="actions" style={{ verticalAlign:'middle', paddingLeft: 12 }}>
                    <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'flex-start', gap:8, width:'100%', minHeight:56 }}>
                      <button className="btn" onClick={()=>onEdit(u)}>Edit</button>
                      {u.status === 'active'
                        ? <button className="btn" onClick={()=>deactivate(u)}>Deactivate</button>
                        : <button className="btn" onClick={()=>activate(u)}>Activate</button>}
                      {u.authMethod!=='microsoft'&&<button className="btn" onClick={()=>openResetPassword(u)}>Reset Password</button>}
                      {u.status !== 'active'
                        ? <button className="btn danger" onClick={()=>requestArchiveByEmail(u.email)}>Archive</button>
                        : <span aria-hidden="true" className="btn danger" style={{ visibility:'hidden', pointerEvents:'none' }}>Archive</span>}
                    </div>
                  </td>
                </tr>
              ))}

              {/* archivedRows are only shown when showArchived is true */}
              {showArchived && archivedRows.map((u) => (
                <tr key={(u.email || u.id) + ':archived'}>
                  <td>
                    <div><strong>{u.displayName}</strong></div>
                    <div className="muted">{u.email}</div>
                  </td>
                  <td className="user-title">{u.title || "—"}</td>
                  <td className="user-directory"><DirectoryDetails directory={u.directory}/></td>
                  <td>
                    <div className="pillWrap">
                      {(u.roles||[]).map(r => <span className="pill" key={r}>{r}</span>)}
                    </div>
                  </td>
                  <td>
                    <div className="pillWrap">
                      {(u.communities||[]).map(n => <span className="pill" key={n}>{n}</span>)}
                    </div>
                  </td>
                  <td>
                    <span className="badge" style={{ padding:'2px 10px', minWidth:64, display:'inline-block', textAlign:'center' }}>
                      {u.status}
                    </span>
                  </td>
                  <td className="actions" style={{ verticalAlign:'middle', paddingLeft: 12 }}>
                    <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'flex-start', gap:8, width:'100%', minHeight:56 }}>
                      <button className="btn" onClick={()=>onEdit(u)}>Edit</button>
                      {u.status === 'active'
                        ? <button className="btn" onClick={()=>deactivate(u)}>Deactivate</button>
                        : <button className="btn" onClick={()=>activate(u)}>Activate</button>}
                      {u.authMethod!=='microsoft'&&<button className="btn" onClick={()=>openResetPassword(u)}>Reset Password</button>}
                      {u.status !== 'active'
                        ? <button className="btn danger" onClick={()=>requestArchiveByEmail(u.email)}>Archive</button>
                        : <span aria-hidden="true" className="btn danger" style={{ visibility:'hidden', pointerEvents:'none' }}>Archive</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      <UserAuditHistory />

      {/* Archive dialog */}
      <ConfirmDialog
        open={confirmArch.open}
        title="Archive user"
        message={
          <>
            You’re about to archive <strong>{(users.find(x => x.email?.toLowerCase() === confirmArch.key?.toLowerCase())?.displayName) || confirmArch.key || 'this user'}</strong>.<br />
            Archived users lose access but remain in historical records. You can re-activate later.
          </>
        }
        confirmLabel="Archive User"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={finalizeArchive}
        onCancel={()=>setConfirmArch({ open:false, key:null })}
      />

      {/* Reset password dialog */}
      <ConfirmDialog
        open={resetDlg.open}
        title="Reset Password"
        confirmLabel={resetDlg.working ? 'Setting…' : 'Set Password'}
        cancelLabel="Cancel"
        tone="default"
        onConfirm={confirmResetPassword}
        onCancel={closeResetPassword}
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              Set a temporary password for <strong>{resetDlg.displayName || resetDlg.email}</strong>.
              The user will be required to change it on next login.
            </p>
            <div className="grid2" style={{ gap: 8 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Temporary Password</label>
                <input
                  value={resetDlg.temp}
                  onChange={e=>setResetDlg(s => ({ ...s, temp: e.target.value }))}
                  placeholder="Enter or generate"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap:'wrap' }}>
                  <button type="button" className="secondary" onClick={() => setResetDlg(s => ({ ...s, temp: generateTempPassword() }))}>Generate</button>
                  <button type="button" className="secondary" onClick={async () => { try { await navigator.clipboard.writeText(resetDlg.temp || '') } catch {} }}>Copy</button>
                </div>
              </div>
              {resetDlg.message && <div className="muted" style={{ gridColumn:'1 / -1', marginTop:6 }}>{resetDlg.message}</div>}
            </div>
          </div>
        }
      />
    </div>
  )
}
